import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { GameLeaderboard } from '../components/game/GameLeaderboard'
import { useStudyStore } from '../store/useStudyStore'
import { ArrowLeft, Trophy, TrendingUp, Clock, Award, Gamepad2 } from 'lucide-react'
import Navigation from '../components/common/Navigation'
import Footer from '../components/common/Footer'

/**
 * Trang thống kê và xếp hạng game
 */
const GameStats: React.FC = () => {
  const navigate = useNavigate()
  const { gameResults } = useStudyStore()
  const [selectedClass, setSelectedClass] = useState<string>('all')

  // Tính toán stats tổng quan
  const totalGames = gameResults.length
  const wonGames = gameResults.filter(r => r.won).length
  const perfectGames = gameResults.filter(r => r.percentage === 100).length
  const totalTimeSpent = gameResults.reduce((sum, r) => sum + r.timeSpent, 0)
  const avgScore = gameResults.length > 0 
    ? gameResults.reduce((sum, r) => sum + r.percentage, 0) / gameResults.length 
    : 0

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Quay lại</span>
          </button>

          <div className="bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 rounded-2xl shadow-xl p-8 text-white">
            <div className="flex items-center gap-4 mb-6">
              <Gamepad2 className="w-12 h-12" />
              <div>
                <h1 className="text-4xl font-bold mb-2">📊 Thống Kê Game</h1>
                <p className="text-white/90">Bảng xếp hạng và thành tích của bạn</p>
              </div>
            </div>

            {/* Overall Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <StatCard
                icon={<Gamepad2 className="w-6 h-6" />}
                label="Tổng lượt chơi"
                value={totalGames.toString()}
                color="bg-white/10"
              />
              <StatCard
                icon={<Trophy className="w-6 h-6" />}
                label="Số lần thắng"
                value={wonGames.toString()}
                color="bg-white/10"
              />
              <StatCard
                icon={<Award className="w-6 h-6" />}
                label="Hoàn hảo"
                value={perfectGames.toString()}
                color="bg-white/10"
              />
              <StatCard
                icon={<TrendingUp className="w-6 h-6" />}
                label="Điểm TB"
                value={`${avgScore.toFixed(0)}%`}
                color="bg-white/10"
              />
              <StatCard
                icon={<Clock className="w-6 h-6" />}
                label="Tổng thời gian"
                value={formatTotalTime(totalTimeSpent)}
                color="bg-white/10"
              />
            </div>
          </div>
        </motion.div>

        {/* Class Filter */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mb-6 flex items-center gap-3"
        >
          <span className="text-gray-700 font-medium">Lọc theo lớp:</span>
          <div className="flex gap-2">
            {['all', '10', '11', '12'].map((cls) => (
              <button
                key={cls}
                onClick={() => setSelectedClass(cls)}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  selectedClass === cls
                    ? 'bg-gradient-to-r from-yellow-400 to-orange-500 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                {cls === 'all' ? 'Tất cả' : `Lớp ${cls}`}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Leaderboard */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <GameLeaderboard
            classId={selectedClass === 'all' ? undefined : selectedClass}
            limit={20}
            showFilters={true}
          />
        </motion.div>
      </div>

      <Footer />
    </div>
  )
}

/**
 * Stat Card Component
 */
const StatCard: React.FC<{
  icon: React.ReactNode
  label: string
  value: string
  color: string
}> = ({ icon, label, value, color }) => (
  <div className={`${color} backdrop-blur-sm rounded-xl p-4 text-center`}>
    <div className="flex items-center justify-center gap-2 mb-2">
      {icon}
      <span className="text-sm font-medium opacity-90">{label}</span>
    </div>
    <div className="text-3xl font-bold">{value}</div>
  </div>
)

/**
 * Format total time to hours and minutes
 */
const formatTotalTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`
  }
  return `${minutes}m`
}

export default GameStats

